import { InputOnChangeData, makeStyles, SearchBox, SearchBoxChangeEvent } from '@fluentui/react-components';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SEARCH_DEBOUNCE_TIME: number = 250;

export interface IFormTOCSidebarSearchProps {
    searchText: string;
    onSearchTextChange: (searchText: string) => void;
    total: number;
    disabled?: boolean;
}

const useStyles = makeStyles({
    invisibleAriaSpan: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        width: '1px',
    },
    searchBarContainer: {
        paddingLeft: '20px',
        paddingRight: '20px',
        paddingTop: '5px',
        marginBottom: '10px',
    },
    searchBox: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
    },
});

const FormTOCSidebarSearch: React.FC<IFormTOCSidebarSearchProps> = ({ searchText, onSearchTextChange, total, disabled = false }) => {
    const [displayValue, setDisplayValue] = useState<string>(searchText);
    const { t } = useTranslation();
    const styles = useStyles();

    const onSearchTextChangeDebounced = useMemo(
        () =>
            debounce((nextSearchText: string | undefined): void => {
                onSearchTextChange(nextSearchText ?? '');
            }, SEARCH_DEBOUNCE_TIME),
        [onSearchTextChange]
    );

    useEffect(() => {
        setDisplayValue(searchText);
        onSearchTextChangeDebounced.cancel();
    }, [searchText, onSearchTextChangeDebounced]);

    useEffect(() => {
        return () => {
            onSearchTextChangeDebounced.cancel();
        };
    }, [onSearchTextChangeDebounced]);

    const onSearchBoxChange = (_ev: SearchBoxChangeEvent | undefined, data: InputOnChangeData) => {
        const nextValue: string = data.value ?? '';
        setDisplayValue(nextValue);

        if (onSearchTextChangeDebounced) {
            onSearchTextChangeDebounced.cancel();
        }

        onSearchTextChangeDebounced(nextValue);
    };

    const onClear = () => {
        setDisplayValue('');
        onSearchTextChange('');
        onSearchTextChangeDebounced.cancel();
    };

    return (
        <div className={styles.searchBarContainer}>
            <SearchBox
                className={styles.searchBox}
                placeholder={t('search').toString()}
                onChange={onSearchBoxChange}
                dismiss={{ onClick: onClear }}
                value={displayValue}
                disabled={disabled}
                aria-label={t('search').toString()}
            />
            <span aria-live='polite' className={styles.invisibleAriaSpan}>
                {t('totalResultsFound', { total: total || t('No') })}
            </span>
        </div>
    );
};

export default FormTOCSidebarSearch;
